import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { BoxLabelPreview, toZpl } from './boxLabelLayout';
import { layoutSenderLabels, readLogoPixels } from './senderLabelLayout';
import { useBoxLabelPrinter } from './useBoxLabelPrinter';
import LabelPreviewFrame, { useLabelSize, previewScale } from './LabelPreviewFrame';

// "the phone and logo"
const listed = (names) => `the ${names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0]}`;

// A recorded Shipment's Sender Labels (issue #451): one per Parcel, for the
// outside of each box, printed in one go to the packing station's Zebra
// printer for the chosen size. See senderLabelLayout.js for what's on them.
//
// Opens at the tenant's default Label Size; staff can choose another and
// print, as often as they like, or close without printing. Nothing in Optim
// needs them printed.
function SenderLabelsView({ shipmentId, onClose }) {
  const [data, setData] = useState(null);
  // undefined until it's been fetched; null when there's none to show.
  const [logo, setLogo] = useState(undefined);
  const [error, setError] = useState('');
  const [printing, setPrinting] = useState(false);
  const [printed, setPrinted] = useState('');
  const sizing = useLabelSize(data?.default_size);
  const { size, dpi } = sizing;
  const { printZpl, problemEl, chooserEl } = useBoxLabelPrinter();

  useEffect(() => {
    let live = true;
    setData(null);
    setLogo(undefined);
    setError('');
    axios.get(`/api/fulfilment/sender-labels/${shipmentId}`)
      .then(async (res) => {
        const found = res.data.data;
        if (live) setData(found);
        // Through axios and a local copy, so the canvas can read the pixels
        // however the media files are hosted.
        const pixels = found.sender.logo_id
          ? await axios.get(`/api/media-images/${found.sender.logo_id}/file`, { responseType: 'blob' })
            .then((file) => readLogoPixels(file.data))
            .catch(() => null)
          : null;
        if (live) setLogo(pixels);
      })
      .catch((err) => { if (live) setError(err.response?.data?.message || 'Could not load the Sender Labels'); });
    return () => { live = false; };
  }, [shipmentId]);

  const layoutAt = (at) => layoutSenderLabels({ sender: data.sender, orderNumber: data.order_number, parcels: data.parcels, logo, size, dpi: at });
  const preview = useMemo(
    () => (data && size && logo !== undefined ? layoutAt(dpi) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, logo, size, dpi],
  );

  const count = data?.parcels || 0;
  const handlePrint = async () => {
    setPrinting(true);
    setPrinted('');
    try {
      const result = await printZpl(size, (at) => layoutAt(at).labels.map(toZpl).join('\n'));
      if (result) setPrinted(`${count} Sender ${count === 1 ? 'Label' : 'Labels'} printed on ${result.printer.name}`);
    } catch {
      // problemEl says what went wrong.
    }
    setPrinting(false);
  };

  return (
    <LabelPreviewFrame
      title="Sender Labels"
      subtitle={data ? [data.display_number, `${count} ${count === 1 ? 'label' : 'labels'}, one per Parcel`].filter(Boolean).join(' · ') : null}
      sizing={sizing}
      onSize={(id) => { sizing.setSizeId(id); setPrinted(''); }}
      onPrint={handlePrint}
      canPrint={!!preview && count > 0 && !printing}
      printing={printing}
      onClose={onClose}
    >
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {!preview && !error && !(data && !size) && <div>Loading...</div>}
      {data && !size && <div style={{ color: '#b91c1c' }}>No Label Sizes are set up. Add one on Warehouse › Label Sizes.</div>}
      {problemEl && <div style={{ marginBottom: 12 }}>{problemEl}</div>}
      {printed && <div style={{ marginBottom: 12, color: '#1e7e34', fontWeight: 600, fontSize: 13 }}>{printed}</div>}
      {preview?.left_off.length > 0 && (
        <div style={{ marginBottom: 12, fontSize: 13, color: '#7a5c00', background: '#fff8e1', border: '1px solid #f0c34d', borderRadius: 8, padding: '8px 12px' }}>
          Everything doesn&apos;t fit on a {size.name} label, so it leaves off {listed(preview.left_off)}. Print it as is, or choose a bigger size.
        </div>
      )}
      {preview && count === 0 && <div style={{ color: '#666' }}>This shipment has no Parcels to label.</div>}
      {preview && count > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16, padding: 16, background: '#eceef1', borderRadius: 6, overflowX: 'auto' }}>
          {preview.labels.map((layout, i) => (
            <div key={i} style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.25)' }}>
              <BoxLabelPreview layout={layout} scale={previewScale(size)} />
            </div>
          ))}
        </div>
      )}
      {chooserEl}
    </LabelPreviewFrame>
  );
}

export default SenderLabelsView;
